import { Button } from 'components/ui/button';
import { DeliveryVoucher } from 'contracts/DeliveryVouchers';
import * as Drop from 'components/ui/dropdown-menu';
import { format } from 'date-fns';
import * as React from 'react';
import {
  ConfirmationDialog,
  ConfirmationDialogRef,
} from 'components/ui/confirmation-dialog';
import {
  RejectionProfDialog,
  RejectionProfDialogRef,
} from '../rejection-voucher-dialog';
import { RejectionProfPayload } from 'contracts/delivery-vouchers/delivery-voucher-justifications';
import { useDeliveryVoucherStore } from '../../hooks/use-delivery-voucher-store';
import { ActivityIndicator } from 'styles/components';

export const Title = ({ children, ...props }: React.ComponentProps<'h3'>) => {
  return (
    <h3 {...props} className={'text-gray-600 text-lg font-semibold leading-6'}>
      {children}
    </h3>
  );
};

export const VoucherInput = ({
  onChange,
  file,
  onUpload,
  loading,
}: React.ComponentProps<'input'> & {
  file: File | null;
  loading?: boolean;
  onUpload: () => void;
}) => {
  return (
    <div className="flex items-center gap-3">
      <label className="flex-1 cursor-pointer rounded-md border border-dashed px-4 py-3 text-sm text-muted-foreground transition-colors hover:bg-muted/50">
        <input
          type="file"
          className="hidden"
          disabled={loading}
          onChange={onChange}
          accept=".pdf,.jpg,.jpeg,.png"
        />

        {file ? file.name : 'Selecione um arquivo'}
      </label>
      <Button
        type="button"
        disabled={!file || loading}
        onClick={onUpload}
      >
        {loading ? <ActivityIndicator /> : 'Enviar'}
      </Button>
    </div>
  );
};

const statusConfig: Record<
  DeliveryVoucher['status'],
  { label: string; className: string }
> = {
  approved: {
    label: 'Aprovado',
    className: 'text-green-600',
  },
  rejected: {
    label: 'Reprovado',
    className: 'text-red-600',
  },
  pending: {
    label: 'Pendente',
    className: 'text-yellow-600',
  },
};

type DeliveryVoucherListProps = {
  vouchers: Array<DeliveryVoucher>;
  onDownload: (voucher: DeliveryVoucher) => void;
  onApprove: (voucher: DeliveryVoucher) => void;
  onReject: (rejectionPayload: RejectionProfPayload, callback: ()=> void) => void;
  onView: (voucher: DeliveryVoucher) => void;
  loading?: boolean;
  canDownload: boolean;
  canUpdate: boolean;
};

export const DeliveryVoucherList = ({
  vouchers,
  onDownload,
  onApprove,
  onReject,
  onView,
  loading,
  canDownload,
  canUpdate,
}: DeliveryVoucherListProps) => {
  const rejectionRef = React.useRef<RejectionProfDialogRef>(null);
  const { setDeliveryVoucher } = useDeliveryVoucherStore();
  const dialogRef = React.useRef<ConfirmationDialogRef>(null);

  const handleApprove = React.useCallback(
    async (voucher: DeliveryVoucher): Promise<void> => {
      const confirmed = await dialogRef.current?.openDialog({
        title: 'Deseja realmente aprovar este canhoto?',
        message: 'Esta ação não poderá ser desfeita após confirmada.',
      });

      if (confirmed === 'confirmed') {
        onApprove(voucher);
      }
    },
    [onApprove, onReject]
  );

  return (
    <>
      <div className="divide-y rounded-md border">
        {vouchers.map((voucher) => {
          const status = statusConfig[voucher.status];
          const updateDisabled = voucher.status !== 'pending';
          const hasActions = canUpdate || canDownload;

          const statusDate = {
            approved: voucher.approvedAt,
            rejected: voucher.rejectedAt,
            pending: null,
          }[voucher.status || 'pending'];

          const formattedDate =
            statusDate && format(new Date(statusDate), 'dd/MM/yyyy HH:mm');

          return (
            <div key={voucher.id} className="flex items-center gap-4 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">
                  {voucher.fileName}
                </p>

                <span className={`text-xs font-medium ${status.className}`}>
                  {status.label}
                </span>
                {formattedDate && (
                  <>
                    <span className="text-xs text-muted-foreground">•</span>
                    <span className="text-xs text-muted-foreground">
                      em {formattedDate}
                    </span>
                  </>
                )}
              </div>

              {hasActions && (
              <Drop.DropdownMenu>
                <Drop.DropdownMenuTrigger asChild disabled={loading}>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Ações para ${voucher.fileName}`}
                  >
                    <span className="text-lg leading-none">⋮</span>
                  </Button>
                </Drop.DropdownMenuTrigger>

                <Drop.DropdownMenuContent align="end">
                  <Drop.DropdownMenuItem
                    className="cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      onView(voucher);
                    }}
                  >
                    Visualizar
                  </Drop.DropdownMenuItem>

                  {canUpdate && (
                    <>
                      <Drop.DropdownMenuItem
                        className="cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          !updateDisabled && handleApprove(voucher);
                        }}
                        disabled={updateDisabled}
                      >
                        Aprovar
                      </Drop.DropdownMenuItem>

                      <Drop.DropdownMenuItem
                        className="cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeliveryVoucher(voucher);
                          !updateDisabled && rejectionRef.current?.open()
                        }}
                        disabled={updateDisabled}
                      >
                        Reprovar
                      </Drop.DropdownMenuItem>
                    </>
                  )}

                  {canUpdate && canDownload && <Drop.DropdownMenuSeparator />}

                  {canDownload && (
                    <Drop.DropdownMenuItem
                      className="cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDownload(voucher);
                      }}
                    >
                      Download
                    </Drop.DropdownMenuItem>
                  )}
                </Drop.DropdownMenuContent>
              </Drop.DropdownMenu>
              )}
            </div>
          );
        })}
      </div>
      <RejectionProfDialog
        ref={rejectionRef}
        onSubmit={onReject}
        loading={loading}
      />
      <ConfirmationDialog ref={dialogRef} />
    </>
  );
};
